/**
 * Campo de lectura para escáner en modo teclado (Zebra DS2208 y similares).
 *
 * Resuelve la captura y se la entrega al padre ya limpia, una lectura por vez:
 *   - Separador GS1 (FNC1). El lector lo transmite como "código Alt" de
 *     Windows: NumLock · Alt+Numpad 0 0 2 9 · NumLock. Los dígitos con Alt se
 *     acumulan y se bloquea su inserción como texto; el código se cierra con la
 *     PRIMERA TECLA QUE NO ES PARTE DE ÉL (NumLock, la tecla siguiente del
 *     texto o Enter), no con el keyup de Alt, porque el lector a veces suelta y
 *     vuelve a apretar Alt en medio del código (ver `codigo-alt.ts`). Al
 *     cerrar se inserta el separador visible `␝` de forma sincrónica en el
 *     DOM, antes de que el navegador inserte la tecla que lo cerró. Si el
 *     sistema además compone su propio GS, el parser tolera los dos seguidos.
 *     También se acepta Ctrl+] (lectores con Function Key Mapping).
 *   - Ráfaga. Las teclas del lector llegan a menos de `UMBRAL_RAFAGA_MS` una
 *     de otra, algo que una persona no sostiene. Esa distinción gobierna las
 *     dos reglas siguientes; `rafaga-lector.ts` la comparte con el modal.
 *   - Distribución de teclado. El lector manda teclas físicas y la PC las
 *     traduce con su distribución; si no coinciden (Zebra emula US, la PC está
 *     en español), la tecla del `-` llega como `'`. No se puede saber qué
 *     emula cada lector, así que el campo produce TRES interpretaciones de la
 *     misma ráfaga: la principal, decodificada por tecla física con tabla US
 *     (`teclado-us.ts`), va al campo; las alternativas, el texto tal como lo
 *     produjo la PC y la decodificación con tabla española (`teclado-es.ts`),
 *     viajan en `MetaLectura.alternativas`. La lógica de pickeo prueba las
 *     tres contra los lotes del remito del ERP. Las alternativas solo se
 *     entregan si la lectura fue una ráfaga pura, sin pegados ni ediciones (se
 *     detecta por largo). El tipeo humano queda intacto.
 *   - Teclas de control en ráfaga. Algunos lectores transmiten el GS como una
 *     tecla de control: la Syble XB-6266MBT del laboratorio manda Escape,
 *     confirmado en las trazas del laboratorio (2026-09-17) exactamente en la
 *     posición del FNC1. Un Escape humano vacía el campo; uno del lector,
 *     dentro de una ráfaga, se convierte en el separador `␝`, igual que el
 *     código Alt de los Zebra. Tab en ráfaga se bloquea para que el foco no se
 *     vaya del campo.
 *   - Velocidad. Al Enter, la lectura se saca del campo EN EL MISMO EVENTO y el
 *     campo queda vacío antes de la primera tecla del escaneo siguiente. La
 *     lectura entra a una cola que se drena en un microtask, fuera del handler
 *     de teclado; si se acumulan, se muestra "N en cola".
 *   - Traza de teclas. Por cada lectura se anota la secuencia de eventos de
 *     teclado (key, code, modificadores y ms entre teclas) y se entrega junto
 *     con el texto en `onLectura`. Es material de diagnóstico: cada lector,
 *     sistema operativo y distribución de teclado transmite distinto, y el
 *     texto final ya perdió lo que el navegador descartó.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { ScanLine } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { GS1_SEPARADOR_VISIBLE } from '@/domain/gs1';
import type { TeclaLectura } from '@/api/endpoints/pickeo-log';
import { pasoCodigoAlt } from './codigo-alt';
import { caracterUs } from './teclado-us';
import { caracterEs } from './teclado-es';
import { UMBRAL_RAFAGA_MS, anotarTeclaDeRafaga } from './rafaga-lector';

/** Lo que acompaña a cada lectura: traza de teclas, duración y otras interpretaciones. */
export interface MetaLectura {
  teclas: TeclaLectura[];
  /** ms entre la primera tecla y el Enter. Null si no se anotó ninguna tecla. */
  duracionMs: number | null;
  /**
   * Otras interpretaciones del mismo texto, para probar si la principal no
   * identifica la unidad: tal como lo produjo la PC y decodificado con tabla
   * española, en ese orden, sin repetidos ni iguales a la principal. Vacío si
   * la lectura no fue una ráfaga pura (pegado, ediciones a mano).
   */
  alternativas: string[];
}

/** Tope de eventos anotados por lectura; un DataMatrix son ~100–150. */
const MAX_TECLAS = 1000;

interface Props {
  inputRef: RefObject<HTMLInputElement>;
  /** Recibe cada lectura completa (texto tal como llegó, con `␝` si hubo separador) y su traza. */
  onLectura: (raw: string, meta: MetaLectura) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Cambiar este valor limpia el campo (ej. al reabrir el modal). */
  resetKey?: unknown;
}

interface LecturaEncolada {
  raw: string;
  meta: MetaLectura;
}

export function CampoLecturaGs1({
  inputRef,
  onLectura,
  disabled = false,
  placeholder = 'Escaneá el DataMatrix o ingresá el N° de serie y presioná Enter',
  resetKey,
}: Props) {
  const [valor, setValor] = useState('');

  // ── Traza de teclas ───────────────────────────────────────────────────────
  const teclas = useRef<TeclaLectura[]>([]);
  const ultimaTeclaTs = useRef<number | null>(null);

  /** Anota el evento y devuelve cuánto pasó desde la tecla anterior. */
  const anotarTecla = (
    e: KeyboardEvent<HTMLInputElement>,
    up = false,
  ): { dt: number; primera: boolean } => {
    const ahora = performance.now();
    const primera = ultimaTeclaTs.current == null;
    const dt = primera ? 0 : Math.max(0, Math.round(ahora - (ultimaTeclaTs.current ?? ahora)));
    ultimaTeclaTs.current = ahora;
    anotarTeclaDeRafaga(ahora);
    if (teclas.current.length < MAX_TECLAS) {
      const t: TeclaLectura = { k: e.key.slice(0, 32), c: e.code.slice(0, 32), dt };
      if (e.altKey) t.a = true;
      if (e.ctrlKey) t.ct = true;
      if (e.shiftKey) t.s = true;
      if (e.metaKey) t.m = true;
      if (up) t.up = true;
      teclas.current.push(t);
    }
    return { dt, primera };
  };

  // ── Interpretaciones alternativas de la ráfaga ────────────────────────────
  // El campo (DOM) lleva la interpretación US. En paralelo se acumulan el texto
  // tal como lo produjo la PC y la decodificación con tabla española.
  const bufHost = useRef('');
  const bufEs = useRef('');

  const anotarVariantes = (host: string, es: string) => {
    bufHost.current += host;
    bufEs.current += es;
  };

  /**
   * Cierra la lectura: devuelve traza, duración y alternativas, y reinicia todo
   * para la próxima. Una alternativa vale solo si tiene el mismo largo que el
   * texto principal (ráfaga pura, sin pegados ni borrados) y aporta algo nuevo.
   */
  const cerrarLectura = (raw: string, pendiente: string): MetaLectura => {
    const lista = teclas.current;
    const candidatas = [bufHost.current + pendiente, bufEs.current + pendiente];
    teclas.current = [];
    ultimaTeclaTs.current = null;
    bufHost.current = '';
    bufEs.current = '';
    // La primera tecla tiene dt=0; la suma del resto es primera tecla → Enter.
    const duracionMs = lista.length > 0 ? lista.reduce((s, t) => s + t.dt, 0) : null;
    const alternativas = candidatas.filter(
      (t, i, arr) => t.length === raw.length && t !== raw && arr.indexOf(t) === i,
    );
    return { teclas: lista, duracionMs, alternativas };
  };

  // ── Código Alt → separador ────────────────────────────────────────────────
  // Dígitos del código en curso. La máquina de estados vive en `codigo-alt.ts`;
  // acá solo se aplica su decisión sobre el evento y el campo.
  const codigoAlt = useRef('');

  useEffect(() => {
    setValor('');
    teclas.current = [];
    ultimaTeclaTs.current = null;
    bufHost.current = '';
    bufEs.current = '';
    codigoAlt.current = '';
  }, [resetKey]);

  /**
   * Inserta texto al final del campo de forma sincrónica (DOM y estado). Si el
   * separador quedara solo en el estado, la tecla que cerró el código se
   * insertaría en el DOM antes del render y el `onChange` lo pisaría. Las
   * variantes reciben lo mismo salvo que se indique otra cosa.
   */
  const insertarAlFinal = (
    input: HTMLInputElement,
    texto: string,
    variantes: { host: string; es: string } = { host: texto, es: texto },
  ) => {
    if (!texto) return;
    const nuevo = input.value + texto;
    input.value = nuevo;
    setValor(nuevo);
    anotarVariantes(variantes.host, variantes.es);
  };

  // ── Cola de lecturas ──────────────────────────────────────────────────────
  const cola = useRef<LecturaEncolada[]>([]);
  const drenajeProgramado = useRef(false);
  const [enCola, setEnCola] = useState(0);

  const drenar = useCallback(() => {
    drenajeProgramado.current = false;
    let item: LecturaEncolada | undefined;
    while ((item = cola.current.shift()) !== undefined) {
      onLectura(item.raw, item.meta);
    }
    setEnCola(0);
  }, [onLectura]);

  const encolar = (item: LecturaEncolada) => {
    cola.current.push(item);
    setEnCola(cola.current.length);
    if (!drenajeProgramado.current) {
      drenajeProgramado.current = true;
      queueMicrotask(drenar);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const { dt, primera } = anotarTecla(e);
    const enRafaga = !primera && dt < UMBRAL_RAFAGA_MS;
    const input = e.currentTarget;

    const paso = pasoCodigoAlt(codigoAlt.current, e);
    codigoAlt.current = paso.buffer;
    if (paso.decision.tipo === 'absorbida') {
      e.preventDefault();
      return;
    }
    if (paso.decision.tipo === 'modificador') return;
    // Cualquier otra tecla cerró el código en curso (si había): lo que haya
    // que insertar va antes de procesar la tecla.
    const pendiente = paso.decision.insertar;

    if (e.code === 'NumLock') {
      e.preventDefault();
      insertarAlFinal(input, pendiente);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const raw = input.value + pendiente;
      input.value = '';
      setValor('');
      const meta = cerrarLectura(raw, pendiente);
      if (raw.trim()) encolar({ raw, meta });
      return;
    }
    insertarAlFinal(input, pendiente);
    if (e.ctrlKey && (e.code === 'BracketRight' || e.key === ']')) {
      e.preventDefault();
      insertarAlFinal(input, GS1_SEPARADOR_VISIBLE);
      return;
    }

    // ── Teclas de control ─────────────────────────────────────────────────
    if (e.key === 'Escape') {
      // Del lector: es su forma de mandar el GS, así que se inserta el
      // separador. Humano con texto: vaciar y empezar de nuevo. Humano sin
      // texto: no se previene, y el modal se cierra como siempre.
      if (enRafaga) {
        e.preventDefault();
        insertarAlFinal(input, GS1_SEPARADOR_VISIBLE);
        return;
      }
      if (valor) {
        e.preventDefault();
        setValor('');
        cerrarLectura('', '');
      }
      return;
    }
    if (e.key === 'Tab' && enRafaga) {
      e.preventDefault();
      return;
    }

    // ── Texto: interpretación principal (US) y variantes ──────────────────
    // Con atajos (Ctrl+V, etc.) el navegador hace lo suyo y las variantes se
    // invalidan solas por largo. Teclas sin carácter (flechas, Backspace) no
    // aportan a las variantes; si cambian el campo, también las invalidan.
    const sinAtajos = !e.ctrlKey && !e.metaKey && !e.altKey && !e.nativeEvent.isComposing;
    if (!sinAtajos) return;
    const charHost = e.key.length === 1 ? e.key : '';
    if (!enRafaga) {
      anotarVariantes(charHost, charHost);
      return;
    }
    const us = caracterUs(e.code, e.shiftKey);
    const es = caracterEs(e.code, e.shiftKey) ?? charHost;
    if (us != null && us !== e.key) {
      e.preventDefault();
      insertarAlFinal(input, us, { host: charHost, es });
      return;
    }
    anotarVariantes(charHost, es);
  };

  return (
    <div className="relative">
      <ScanLine className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <Input
        ref={inputRef}
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={onKeyDown}
        onKeyUp={(e) => {
          // Solo se anota: el keyup de Alt ya no cierra el código (ver arriba).
          if (e.key === 'Alt') anotarTecla(e, true);
        }}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        className="pl-9 font-mono"
      />
      {enCola > 0 && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-500">
          {enCola} en cola
        </span>
      )}
    </div>
  );
}
